<script setup lang="ts">
  import { ref, computed, watch } from "vue";
  import type { TPickupPoint } from "../types";
  import PickupPointDetail from "./detail.vue";

  interface Props {
    pickupPoints: TPickupPoint[];
    selectedPickupPoint?: TPickupPoint | null;
    deliveryTypeId: number;
  }

  const props = withDefaults(defineProps<Props>(), {
    pickupPoints: () => [],
    selectedPickupPoint: null,
  });

  // Emits
  const emit = defineEmits<{
    "pickup-point-selected": [pickupPoint: TPickupPoint];
    "pickup-point-confirmed": [pickupPoint: TPickupPoint];
    "search-results": [pickupPoints: TPickupPoint[]];
  }>();

  const searchQuery = ref("");
  const showDetail = ref(false);
  const searchResults = ref<TPickupPoint[]>([]);
  const isSearching = ref(false);
  const hasSearched = ref(false);

  let searchTimeout: NodeJS.Timeout | null = null;

  // Watch for selectedPickupPoint changes - show detail when changing from null to a pickup point
  watch(
    () => props.selectedPickupPoint,
    (newValue, oldValue) => {
      if (oldValue === null && newValue !== null) {
        showDetail.value = true;
      }
      if (newValue === null) {
        showDetail.value = false;
      }
    },
  );

  // Watch for search query changes with debounce
  watch(searchQuery, (newQuery) => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (newQuery.length < 2) {
      searchResults.value = [];
      hasSearched.value = false;
      isSearching.value = false;
      return;
    }

    isSearching.value = true;
    searchTimeout = setTimeout(async () => {
      await performSearch(newQuery);
    }, 200);
  });

  // Perform server-side search
  const performSearch = async (query: string) => {
    try {
      const url = `/api/v1/balikobot-branches/available/${
        props.deliveryTypeId
      }?q=${encodeURIComponent(query)}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      searchResults.value = data.map(
        (point: any) =>
          <TPickupPoint>{
            id: point.branch_id,
            name: point.name,
            city: point.city,
            street: point.street,
            zip: point.zip,
            country: point.country,
            lat: point.lat,
            lon: point.lon,
            opening: point.opening,
          },
      );

      hasSearched.value = true;
      emit("search-results", searchResults.value);
    } catch (error) {
      console.error("Error performing search:", error);
      searchResults.value = [];
      hasSearched.value = true;
    } finally {
      isSearching.value = false;
    }
  };

  // Handle pickup point selection
  const handlePickupPointClick = (pickupPoint: TPickupPoint) => {
    emit("pickup-point-selected", pickupPoint);
    showDetail.value = true;
  };

  // Handle pickup point detail actions
  const handleDetailClose = () => {
    showDetail.value = false;
  };

  const handleDetailSelect = (pickupPoint: TPickupPoint) => {
    emit("pickup-point-confirmed", pickupPoint);
    showDetail.value = false;
  };

  // Computed property for displayed pickup points
  const displayedPickupPoints = computed(() => {
    if (hasSearched.value && searchQuery.value.length >= 2) {
      return searchResults.value;
    }
    return props.pickupPoints;
  });
</script>

<template>
  <div class="bb-pickup-point-info-tab">
    <!-- Search Field -->
    <div class="search-container">
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Vyhledat výdejní místa..."
        class="search-input"
      />
      <div v-if="isSearching" class="search-status">Vyhledávám...</div>
      <div
        v-else-if="hasSearched && searchQuery.length >= 2 && searchResults.length === 0"
        class="search-status"
      >
        Žádné výsledky nenalezeny
      </div>
    </div>

    <!-- Pickup Points List -->
    <div class="pickup-points-list" v-if="!showDetail">
      <div
        v-for="point in displayedPickupPoints.slice(0, hasSearched ? undefined : 10)"
        :key="point.id"
        class="pickup-point-item"
        :class="{ selected: selectedPickupPoint?.id === point.id }"
        @click="handlePickupPointClick(point)"
      >
        <div class="pickup-point-details">
          <p class="name">{{ point.name }}</p>
          <p class="street">{{ point.street }}, {{ point.city }}, {{ point.zip }}</p>
        </div>
      </div>

      <div
        v-if="displayedPickupPoints.length === 0 && !isSearching && !hasSearched"
        class="no-results"
      >
        <p>Žádná výdejní místa v současném zobrazení</p>
      </div>
    </div>

    <!-- Pickup Point Detail Component - moved outside of scrollable container -->
    <PickupPointDetail
      v-if="showDetail && selectedPickupPoint"
      :pickup-point="selectedPickupPoint"
      @close="handleDetailClose"
      @select="handleDetailSelect"
    />
  </div>
</template>

<style scoped lang="scss">
  .bb-pickup-point-info-tab {
    height: 100%;
    display: flex;
    flex-direction: column;

    .search-container {
      margin-bottom: 10px;

      .search-input {
        width: 100%;
        padding: 6px 15px;
        border: 2px solid #ddd;
        border-radius: 3px;
        font-size: 14px;

        &:focus {
          outline: none;
          border-color: #3399cc;
        }
      }

      .search-status {
        margin-top: 8px;
        font-size: 12px;
        color: #666;
        text-align: center;
        font-style: italic;
      }
    }

    .pickup-points-list {
      flex: 1;
      overflow-y: auto;
      position: relative;

      .pickup-point-item {
        padding: 10px;
        border: 1px solid #e0e0e0;
        margin-bottom: 0;
        background: white;
        cursor: pointer;
        transition: all 0.2s ease;
        border-top: none;
        position: relative;
        overflow: hidden;
        padding-right: 30px;

        &::after {
          position: absolute;
          top: 0;
          right: 0;
          height: 100%;
          width: 0px;
          color: #fff;
          font-size: 14px;
          font-weight: bold;
          content: "»";
          display: flex;
          justify-content: center;
          align-items: center;
          transition: all 0.2s;
        }

        &:first-child {
          border-radius: 3px 3px 0 0;
          border-top: 1px solid #e0e0e0;
        }

        &:last-child {
          border-radius: 0 0 3px 3px;
        }

        &:hover {
          background-color: #f4f4f4;
          padding-right: 30px;

          .pickup-point-details {
            p.name {
              color: #3399cc;
            }
          }

          &::after {
            width: 25px;
            transition: all 0.2s;
            background-color: #3399cc;
          }
        }

        &.selected {
          background: #f4f4f4;

          .pickup-point-details {
            p.name {
              color: #2b81ad;
            }
          }

          &::after {
            width: 25px;
            transition: all 0.2s;

            background-color: #2b81ad;
          }
        }

        .pickup-point-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 8px;

          .pickup-point-name {
            margin: 0;
            font-size: 16px;
            font-weight: 600;
            color: #333;
            flex: 1;
          }

          .distance {
            font-size: 12px;
            color: #666;
            background: #f0f0f0;
            padding: 2px 6px;
            border-radius: 12px;
            margin-left: 8px;
          }
        }

        .pickup-point-details {
          p {
            font-size: 1.3rem;
            margin-bottom: 0.2rem;
            &.name {
              font-size: 1.5rem;
              font-weight: bold;
            }
          }
          .address {
            margin: 0 0 4px 0;
            font-size: 14px;
            color: #555;
          }

          .city-zip {
            margin: 0 0 4px 0;
            font-size: 14px;
            color: #555;
          }

          .opening-hours {
            margin: 0;
            font-size: 12px;
            color: #777;
            font-style: italic;
          }
        }
      }

      .no-results {
        text-align: center;
        padding: 24px;
        color: #666;

        p {
          margin: 0;
          font-style: italic;
        }
      }
    }
  }

  @media (max-width: 579.98px) {
    .bb-pickup-point-info-tab .pickup-points-list .pickup-point-item {
      padding: 4px 10px;
      .pickup-point-details {
        p {
          &.name {
            font-size: 1.3rem;
            margin-bottom: 0;
          }

          &.street {
            font-size: 1.2rem;
            margin-bottom: 0;
          }
        }
      }
    }
  }
</style>
